import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ReceiptText, Printer, FileDown, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function StaffKOTs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const params = () => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (fromDate) p.set("from_date", fromDate);
    if (toDate) p.set("to_date", toDate);
    return p.toString();
  };

  const load = async () => {
    setLoading(true);
    try {
      const qs = params();
      const url = `/staff/briefs?limit=500${qs ? `&${qs}` : ""}`;
      const { data } = await api.get(url);
      setItems(data);
    } catch { setItems([]); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = (e) => { e.preventDefault(); load(); };

  const stats = useMemo(() => {
    const events = new Set();
    let totalDishes = 0, totalPax = 0, totalAmount = 0;
    items.forEach((b) => {
      totalDishes += (b.dish_count || 0);
      totalPax += (b.pax || 0);
      totalAmount += (b.approx_total || 0);
      if (b.event_name) events.add(b.event_name.trim().toLowerCase());
    });
    return { count: items.length, events: events.size, totalDishes, totalPax, totalAmount };
  }, [items]);

  const setPreset = (preset) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    if (preset === "today") { setFromDate(fmt(today)); setToDate(fmt(today)); }
    else if (preset === "7d") {
      const s = new Date(today); s.setDate(s.getDate() - 6);
      setFromDate(fmt(s)); setToDate(fmt(today));
    } else if (preset === "month") {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      const e = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setFromDate(fmt(s)); setToDate(fmt(e));
    } else if (preset === "last_month") {
      const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const e = new Date(today.getFullYear(), today.getMonth(), 0);
      setFromDate(fmt(s)); setToDate(fmt(e));
    } else { setFromDate(""); setToDate(""); }
  };

  const downloadBlob = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Download failed");
    }
  };

  const downloadOne = (b) => downloadBlob(`/staff/briefs/${b.id}/pdf`, `TBB-${b.kot_label}.pdf`);
  const downloadCsv = () => {
    const qs = params();
    downloadBlob(`/staff/briefs/export.csv${qs ? `?${qs}` : ""}`, `TBB-KOTs-${new Date().toISOString().slice(0,10)}.csv`);
  };

  return (
    <div data-testid="staff-kots">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Kitchen dispatch log</div>
          <h1 className="mt-2 text-4xl">Kitchen Order Tickets</h1>
          <p className="mt-3 text-[hsl(var(--ink-soft))] max-w-xl">
            Every kitchen brief printed from the back office, newest first. Search by KOT number, event, host, or the staff who printed it.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="kot-export-csv" onClick={downloadCsv} className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-[hsl(var(--brass))] rounded text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors">
            <FileSpreadsheet size={14} /> Export all (CSV)
          </button>
          <Link to="/staff/kitchen-brief" data-testid="kot-new-brief" className="btn-brass !py-2.5 !px-5 text-sm">
            <Printer size={14} /> New brief
          </Link>
        </div>
      </div>

      <form onSubmit={submit} className="mt-8 flex flex-col gap-3">
        <div className="flex gap-2 max-w-xl">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--ink-soft))]" />
            <input
              data-testid="kot-search"
              placeholder="Search KOT-, event, host or email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-[hsl(var(--rule))] rounded bg-[hsl(var(--bg-2))] text-sm focus:outline-none focus:border-[hsl(var(--forest))]"
            />
          </div>
          <button data-testid="kot-search-submit" type="submit" className="px-4 py-2 border border-[hsl(var(--rule))] text-sm rounded hover:border-[hsl(var(--brass))]">Apply</button>
          {(q || fromDate || toDate) && (
            <button type="button" data-testid="kot-clear-filters" onClick={() => { setQ(""); setFromDate(""); setToDate(""); setTimeout(load, 0); }} className="text-sm text-[hsl(var(--ink-soft))] px-2">Clear</button>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap text-sm">
          <label className="flex items-center gap-2">
            <span className="text-[hsl(var(--ink-soft))] text-xs uppercase tracking-widest">From</span>
            <input data-testid="kot-from-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="px-2 py-1.5 border border-[hsl(var(--rule))] rounded bg-[hsl(var(--bg-2))] text-sm" />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-[hsl(var(--ink-soft))] text-xs uppercase tracking-widest">To</span>
            <input data-testid="kot-to-date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="px-2 py-1.5 border border-[hsl(var(--rule))] rounded bg-[hsl(var(--bg-2))] text-sm" />
          </label>
          <div className="flex gap-1 flex-wrap">
            {[["today","Today"],["7d","Last 7 days"],["month","This month"],["last_month","Last month"],["all","All time"]].map(([k, label]) => (
              <button key={k} type="button" data-testid={`kot-preset-${k}`} onClick={() => setPreset(k)} className="text-xs px-3 py-1 border border-[hsl(var(--rule))] rounded hover:border-[hsl(var(--brass))] hover:text-[hsl(var(--brass-2))]">{label}</button>
            ))}
          </div>
        </div>
      </form>

      {/* Stats — computed from currently loaded results */}
      {!loading && items.length > 0 && (
        <div data-testid="kot-stats" className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
          <MiniStat label="Briefs" value={stats.count} />
          <MiniStat label="Unique events" value={stats.events} />
          <MiniStat label="Total pax" value={stats.totalPax} />
          <MiniStat label="Total (₹)" value={`₹${Math.round(stats.totalAmount).toLocaleString("en-IN")}`} accent />
        </div>
      )}

      <div className="mt-6 text-xs text-[hsl(var(--ink-soft))]" data-testid="kot-count">
        {loading ? "Loading…" : `${items.length} ${items.length === 1 ? "brief" : "briefs"} shown`}
      </div>

      {!loading && items.length === 0 && (
        <div data-testid="kot-empty" className="mt-6 p-10 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] text-center text-[hsl(var(--ink-soft))]">
          <ReceiptText size={22} className="mx-auto text-[hsl(var(--brass))]" />
          <div className="mt-3">
            {q ? "No briefs match that search." : "No KOTs yet. Print your first kitchen brief to see it here."}
          </div>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="mt-6 border border-[hsl(var(--rule))] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--bg-2))] text-left text-[10px] uppercase tracking-widest text-[hsl(var(--ink-soft))]">
              <tr>
                <th className="p-3">KOT</th>
                <th className="p-3">Event · Host</th>
                <th className="p-3">Date · Time</th>
                <th className="p-3">Pax</th>
                <th className="p-3">Dishes</th>
                <th className="p-3">Mode</th>
                <th className="p-3">Printed by</th>
                <th className="p-3">When</th>
                  <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} data-testid={`kot-row-${b.kot_label}`} className="border-t border-[hsl(var(--rule))]">
                  <td className="p-3 font-mono font-semibold text-[hsl(var(--forest))]">{b.kot_label}</td>
                  <td className="p-3">
                    <div>{b.event_name || <span className="text-[hsl(var(--ink-soft))] italic">—</span>}</div>
                    {b.host && <div className="text-[10px] text-[hsl(var(--ink-soft))]">host: {b.host}</div>}
                    {b.package_tagline && <div className="text-[10px] text-[hsl(var(--ink-soft))]">{b.package_tagline}</div>}
                  </td>
                  <td className="p-3 text-[hsl(var(--ink-soft))]">
                    {b.date || "—"}{b.time ? ` · ${b.time}` : ""}
                  </td>
                  <td className="p-3">{b.pax || "—"}</td>
                  <td className="p-3">{b.dish_count}</td>
                  <td className="p-3 text-[hsl(var(--ink-soft))] capitalize">{b.mode}</td>
                  <td className="p-3 text-[hsl(var(--ink-soft))]">{b.printed_by}</td>
                  <td className="p-3 text-[hsl(var(--ink-soft))]">{new Date(b.printed_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="p-3">
                    <button data-testid={`kot-download-${b.kot_label}`} onClick={() => downloadOne(b)} title="Download PDF" className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[hsl(var(--rule))] rounded hover:border-[hsl(var(--brass))] hover:text-[hsl(var(--brass-2))]">
                      <FileDown size={12} /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] p-3">
      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--ink-soft))]">{label}</div>
      <div className={`mt-0.5 text-xl font-serif ${accent ? "text-[hsl(var(--forest))]" : ""}`}>{value}</div>
    </div>
  );
}

